import { useCallback, useEffect, useState } from 'react';
import { PublicNavbar } from '../components/PublicNavbar';
import { Footer } from '../components/Footer';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Send, Camera, X } from 'lucide-react';
import defaultHeroImage from '../images/logo/Eddy-logo-carte.png';

const LOGO_MARK = "https://customer-assets.emergentagent.com/job_5c120cb0-6c39-4574-8992-e03c5cf62daa/artifacts/9e7hexs6_logo-ed-removebg-preview.png";

export default function HomePage() {
  const [photos, setPhotos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxImage, setLightboxImage] = useState(null);
  
  // Contact form
  const [contactForm, setContactForm] = useState({
    name: '', email: '', phone: '', subject: '', message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [cats, sett] = await Promise.all([
        api.getCategories(),
        api.getSettings()
      ]);
      setCategories(cats);
      setSettings(sett);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  }, []);

  const loadPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getPhotos(selectedCategory);
      setPhotos(data);
    } catch (error) {
      console.error('Error loading photos:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.submitContact(contactForm);
      toast.success('Message envoyé avec succès !');
      setContactForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background transition-theme">
      <PublicNavbar />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${settings?.hero_image_url || defaultHeroImage})` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 text-center px-6">
          {/* <img 
            src={LOGO_MARK} 
            alt="ED" 
            className="h-24 md:h-32 w-auto mx-auto mb-8 animate-fade-in-up"
          /> */}
          <h1 className="font-serif text-5xl md:text-7xl font-medium text-white tracking-tight animate-fade-in-up animate-delay-100">
            Eddy Dossou
          </h1>
          <p className="mt-4 text-lg md:text-xl text-white/80 tracking-widest uppercase animate-fade-in-up animate-delay-200">
            Photographe
          </p>
          <Button 
            onClick={() => document.getElementById('gallery').scrollIntoView({ behavior: 'smooth' })}
            className="mt-8 rounded-full px-8 py-6 text-sm uppercase tracking-widest animate-fade-in-up animate-delay-300"
            data-testid="hero-cta"
          >
            Découvrir mes travaux
          </Button>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-20 md:py-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-4">Portfolio</p>
            <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight">
              Mes Réalisations
            </h2>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <Button
              variant={selectedCategory === null ? "default" : "outline"}
              onClick={() => setSelectedCategory(null)}
              className="rounded-full"
              data-testid="filter-all"
            >
              Tout
            </Button>
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? "default" : "outline"}
                onClick={() => setSelectedCategory(cat.id)}
                className="rounded-full"
                data-testid={`filter-${cat.slug}`}
              >
                {cat.name}
              </Button>
            ))}
          </div>

          {/* Photos Grid (Masonry) */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : photos.length === 0 ? (
            <div className="text-center py-12">
              <Camera className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucune photo pour le moment</p>
            </div>
          ) : (
            <div className="masonry-grid">
              {photos.map((photo, index) => (
                <div 
                  key={photo.id} 
                  className="masonry-item group cursor-pointer"
                  onClick={() => setLightboxImage(photo)}
                  data-testid={`photo-${index}`}
                >
                  <div className="relative overflow-hidden rounded-sm">
                    <img 
                      src={photo.image_url || photo.image_data} 
                      alt={photo.title}
                      className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                        <p className="text-white font-serif text-lg">{photo.title}</p>
                        <p className="text-white/70 text-xs tracking-widest uppercase">{photo.category_name}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white/70 hover:text-white"
            onClick={() => setLightboxImage(null)}
            data-testid="lightbox-close"
          >
            <X className="h-8 w-8" />
          </button>
          <img 
            src={lightboxImage.image_url || lightboxImage.image_data} 
            alt={lightboxImage.title}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-0 right-0 text-center">
            <p className="text-white font-serif text-xl">{lightboxImage.title}</p>
            <p className="text-white/70 text-sm">{lightboxImage.category_name}</p>
          </div>
        </div>
      )}

      {/* About Section */}
      <section id="about" className="py-20 md:py-32 px-6 lg:px-12 bg-secondary/30">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-4">À propos</p>
          <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight mb-8">
            Qui suis-je ?
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            {settings?.about_text || "Photographe professionnel passionné par la capture de moments uniques. Chaque cliché raconte une histoire, chaque image est une émotion figée dans le temps."}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 md:py-32 px-6 lg:px-12">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-4">Contact</p>
            <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight">
              Travaillons ensemble
            </h2>
          </div>

          <form onSubmit={handleContactSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2 block">Nom</label>
                <Input
                  type="text"
                  required
                  value={contactForm.name}
                  onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                  className="border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary"
                  placeholder="Votre nom"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2 block">Email</label>
                <Input
                  type="email"
                  required
                  value={contactForm.email}
                  onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                  className="border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary"
                  placeholder="votre@email.com"
                  data-testid="contact-email"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2 block">Téléphone</label>
                <Input
                  type="tel"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                  className="border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary"
                  placeholder="(Optionnel)"
                  data-testid="contact-phone"
                />
              </div>
              <div>
                <label className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2 block">Sujet</label>
                <Input
                  type="text"
                  required
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                  className="border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary"
                  placeholder="Votre projet"
                  data-testid="contact-subject"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2 block">Message</label>
              <Textarea
                required
                value={contactForm.message}
                onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                className="border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary min-h-[120px] resize-none"
                placeholder="Décrivez votre projet..."
                data-testid="contact-message"
              />
            </div>
            <div className="text-center pt-4">
              <Button 
                type="submit" 
                disabled={submitting}
                className="rounded-full px-12 py-6 text-sm uppercase tracking-widest"
                data-testid="contact-submit"
              >
                {submitting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Envoyer
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
